function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-coa-pdf="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === '1') resolve();
      else existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.coaPdf = src;
    script.onload = () => {
      script.dataset.loaded = '1';
      resolve();
    };
    script.onerror = () => reject(new Error(`โหลดไลบรารีไม่สำเร็จ: ${src}`));
    document.head.appendChild(script);
  });
}

async function ensurePdfLibs() {
  if (!window.html2canvas) {
    await loadScript('https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js');
  }
  if (!window.jspdf?.jsPDF) {
    await loadScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js');
  }
  return {
    html2canvas: window.html2canvas,
    jsPDF: window.jspdf.jsPDF,
  };
}

function waitForImages(root) {
  const imgs = [...root.querySelectorAll('img')];
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
          setTimeout(done, 2000);
        }),
    ),
  );
}

/** html2canvas ignores object-fit — size img to natural aspect within max box */
function fixImageAspectInClone(clonedDoc) {
  clonedDoc.querySelectorAll('img').forEach((img) => {
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return;

    const parent = img.parentElement;
    const isPhoto = parent?.classList?.contains('coa-photo');
    const isLogo = img.classList.contains('coa-logo-img');
    const isSign = img.classList.contains('coa-sign-img');

    // ~62mm / 16mm / 11mm at 96dpi
    let maxW = parent?.clientWidth || img.clientWidth || nw;
    let maxH = nh;
    if (isPhoto) {
      maxW = Math.min(maxW || 280, parent?.clientWidth || 280);
      maxH = 234; // 62mm — fill width first, then clamp height (keep ratio)
    } else if (isLogo) {
      maxW = 60;
      maxH = 60;
    } else if (isSign) {
      maxW = 136;
      maxH = 42;
    } else {
      maxH = img.clientHeight || img.getBoundingClientRect().height || nh;
    }

    if (!maxW || !maxH) return;

    const ratio = nw / nh;
    let w = maxW;
    let h = w / ratio;
    if (h > maxH) {
      h = maxH;
      w = h * ratio;
    }

    img.style.width = `${Math.round(w)}px`;
    img.style.height = `${Math.round(h)}px`;
    img.style.maxWidth = 'none';
    img.style.maxHeight = 'none';
    img.style.objectFit = 'fill';
    img.style.flex = 'none';
    img.style.alignSelf = 'center';
  });
}

const A4_W_MM = 210;
const A4_H_MM = 297;
// CSS px at 96dpi for stable capture aspect (matches A4)
const A4_W_PX = Math.round((A4_W_MM * 96) / 25.4);
const A4_H_PX = Math.round((A4_H_MM * 96) / 25.4);

/**
 * Capture .coa-page into exactly one A4 PDF — same layout as on-screen / print.
 */
export async function exportCoaPdf(pageEl, filename = 'COA.pdf') {
  if (!pageEl) throw new Error('ไม่พบเอกสาร COA');

  const { html2canvas, jsPDF } = await ensurePdfLibs();

  const prevClass = pageEl.className;
  pageEl.classList.add('is-pdf-capture');

  const prev = {
    width: pageEl.style.width,
    height: pageEl.style.height,
    minHeight: pageEl.style.minHeight,
    maxHeight: pageEl.style.maxHeight,
    overflow: pageEl.style.overflow,
    boxSizing: pageEl.style.boxSizing,
    padding: pageEl.style.padding,
    gap: pageEl.style.gap,
    fontSize: pageEl.style.fontSize,
    boxShadow: pageEl.style.boxShadow,
    margin: pageEl.style.margin,
  };

  pageEl.style.width = `${A4_W_PX}px`;
  pageEl.style.height = `${A4_H_PX}px`;
  pageEl.style.minHeight = `${A4_H_PX}px`;
  pageEl.style.maxHeight = `${A4_H_PX}px`;
  pageEl.style.overflow = 'hidden';
  pageEl.style.boxSizing = 'border-box';
  pageEl.style.padding = '10mm 12mm';
  pageEl.style.gap = '3mm';
  pageEl.style.fontSize = '10px';
  pageEl.style.boxShadow = 'none';
  pageEl.style.margin = '0';

  try {
    await waitForImages(pageEl);
    void pageEl.offsetHeight;
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const canvas = await html2canvas(pageEl, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: A4_W_PX,
      height: A4_H_PX,
      windowWidth: A4_W_PX,
      windowHeight: A4_H_PX,
      onclone: (clonedDoc) => {
        const cloned = clonedDoc.querySelector('.coa-page');
        if (!cloned) return;
        cloned.classList.add('is-pdf-capture');
        cloned.style.width = `${A4_W_PX}px`;
        cloned.style.height = `${A4_H_PX}px`;
        cloned.style.minHeight = `${A4_H_PX}px`;
        cloned.style.maxHeight = `${A4_H_PX}px`;
        cloned.style.overflow = 'hidden';
        cloned.style.boxSizing = 'border-box';
        cloned.style.padding = '10mm 12mm';
        cloned.style.gap = '3mm';
        cloned.style.fontSize = '10px';
        cloned.style.boxShadow = 'none';
        cloned.style.margin = '0';

        const style = clonedDoc.createElement('style');
        style.textContent = `
          .coa-page.is-pdf-capture {
            width: ${A4_W_PX}px !important;
            height: ${A4_H_PX}px !important;
            max-height: ${A4_H_PX}px !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .coa-page.is-pdf-capture .coa-header {
            display: flex !important;
            flex-direction: row !important;
            justify-content: space-between !important;
          }
          .coa-page.is-pdf-capture .coa-docmeta { text-align: right !important; }
          .coa-page.is-pdf-capture .coa-info-grid {
            display: grid !important;
            grid-template-columns: 1.15fr 1fr !important;
            align-items: start !important;
          }
          .coa-page.is-pdf-capture .coa-signs {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr) !important;
            margin-top: auto !important;
          }
          .coa-page.is-pdf-capture .coa-photo {
            max-height: 68mm !important;
            align-items: center !important;
          }
          .coa-page.is-pdf-capture .coa-photo img {
            width: 100% !important;
            height: auto !important;
            max-width: 100% !important;
            max-height: 62mm !important;
            object-fit: contain !important;
            flex: none !important;
          }
        `;
        clonedDoc.head.appendChild(style);

        // Force layout then fix image boxes (object-fit is unreliable in html2canvas)
        void cloned.offsetHeight;
        fixImageAspectInClone(clonedDoc);
      },
    });

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    // Keep aspect ratio — never stretch canvas into A4
    const canvasRatio = canvas.width / canvas.height;
    const pageRatio = A4_W_MM / A4_H_MM;
    let drawW = A4_W_MM;
    let drawH = A4_W_MM / canvasRatio;
    if (drawH > A4_H_MM) {
      drawH = A4_H_MM;
      drawW = A4_H_MM * canvasRatio;
    }
    // Prefer filling page when capture is already A4-shaped (within 1%)
    if (Math.abs(canvasRatio - pageRatio) < 0.01) {
      drawW = A4_W_MM;
      drawH = A4_H_MM;
    }
    const x = (A4_W_MM - drawW) / 2;
    const y = (A4_H_MM - drawH) / 2;

    pdf.addImage(
      canvas.toDataURL('image/jpeg', 0.95),
      'JPEG',
      x,
      y,
      drawW,
      drawH,
      undefined,
      'FAST',
    );
    pdf.save(filename);
  } finally {
    pageEl.className = prevClass;
    pageEl.style.width = prev.width;
    pageEl.style.height = prev.height;
    pageEl.style.minHeight = prev.minHeight;
    pageEl.style.maxHeight = prev.maxHeight;
    pageEl.style.overflow = prev.overflow;
    pageEl.style.boxSizing = prev.boxSizing;
    pageEl.style.padding = prev.padding;
    pageEl.style.gap = prev.gap;
    pageEl.style.fontSize = prev.fontSize;
    pageEl.style.boxShadow = prev.boxShadow;
    pageEl.style.margin = prev.margin;
  }
}
